/**
 * Map marker SVG sprites — one reusable renderer per canonical POI ID.
 *
 * Design: instead of shipping a raster sprite sheet, we build an inline
 * SVG icon per marker DOM element. This is:
 *   • Retina-safe on every DPI
 *   • Small enough to inline (each SVG is 100-200 bytes)
 *   • Themeable — the fill/stroke follow the category color
 *   • Free of a runtime lucide→canvas step (which was flagged as a
 *     performance risk in the P2b directive)
 *
 * The public surface is intentionally tiny:
 *   markerElement({ p, sizePx, onClick })  →  HTMLElement
 *   emergencyAlertElement({ p, onClick })  →  HTMLElement  (distinct visual)
 *
 * NEVER render an emergency-user's precise coordinates through this file —
 * `emergencyAlertElement` is meant for the coarse alert location that the
 * emergency backend deliberately fuzzes for public rendering.
 */

// Per-category monochrome glyph paths (viewBox 24x24). Kept minimal and
// visually consistent — the design language matches lucide-react so
// nothing looks alien beside the layer-switcher chips.
const GLYPHS = {
  restaurant: '<path d="M4 3v18M4 12h4M18 3v18M18 3l3 3v6h-3" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/>',
  hotel:      '<path d="M3 20V6h18v14M3 12h18M7 12V9h4v3" stroke="currentColor" stroke-width="2" fill="none" stroke-linejoin="round"/>',
  fuel:       '<path d="M6 20V5a2 2 0 012-2h6a2 2 0 012 2v15M4 20h16M15 8h3l2 3v6a2 2 0 01-4 0v-4h-1" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/>',
  ev:         '<path d="M13 3L4 14h7l-1 7 9-11h-7l1-7z" stroke="currentColor" stroke-width="2" fill="none" stroke-linejoin="round"/>',
  atm:        '<circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2" fill="none"/><path d="M12 7v10M9 10h6M9 14h6" stroke="currentColor" stroke-width="2" fill="none"/>',
  washroom:   '<path d="M6 8a3 3 0 116 0 3 3 0 01-6 0zM4 21l2-6h4l2 6M14 8h6M17 8v13" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/>',
  hospital:   '<rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" stroke-width="2" fill="none"/><path d="M12 8v8M8 12h8" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/>',
  police:     '<path d="M12 3l9 4v6c0 4.5-3.5 8.5-9 8-5.5.5-9-3.5-9-8V7l9-4z" stroke="currentColor" stroke-width="2" fill="none" stroke-linejoin="round"/><path d="M9 12l2 2 4-4" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/>',
  fire:       '<path d="M12 3c1 4 5 6 5 11a5 5 0 01-10 0c0-2 1-3 2-4-1 3 1 4 3 4-1-3 0-6 0-11z" stroke="currentColor" stroke-width="2" fill="none" stroke-linejoin="round"/>',
  government: '<path d="M3 21h18M4 10v11M20 10v11M8 10v11M16 10v11M12 10v11M2 10h20L12 3 2 10z" stroke="currentColor" stroke-width="2" fill="none" stroke-linejoin="round"/>',
  tourist:    '<path d="M12 21s-7-7-7-12a7 7 0 0114 0c0 5-7 12-7 12z" stroke="currentColor" stroke-width="2" fill="none" stroke-linejoin="round"/><circle cx="12" cy="9" r="2.5" stroke="currentColor" stroke-width="2" fill="none"/>',
  emergency:  '<path d="M12 3a3 3 0 013 3v6l3 3v3H6v-3l3-3V6a3 3 0 013-3z" stroke="currentColor" stroke-width="2" fill="none" stroke-linejoin="round"/><path d="M10 21h4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>',
  // Fallback dot for unknown categories.
  _default:   '<circle cx="12" cy="12" r="4" fill="currentColor"/>',
};

/**
 * Build an <svg> string sized to `px` for a canonical layer id.
 * `color` sets the glyph stroke via `currentColor` — the surrounding
 * badge sets the fill.
 */
function svgFor(id, px = 18) {
  const glyph = GLYPHS[id] || GLYPHS._default;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="${px}" height="${px}" style="color:#fff;display:block">${glyph}</svg>`;
}

// Zoom-adaptive marker size — keeps dense city zoom readable but not
// overwhelming, and grows into a comfortable tap target at high zoom.
export function markerPxForZoom(z = 12) {
  if (z < 12) return 26;   // dense
  if (z >= 16) return 40;  // high zoom
  return 32;               // normal
}

/**
 * Build a DOM element for a normal POI marker.
 *
 *  p         → marker properties (see NearbyMap `markers` useMemo)
 *  sizePx    → outer size in px (adaptive at call site)
 *  onClick   → click handler (parent decides what to do)
 */
export function markerElement({ p, sizePx = 32, onClick }) {
  const el = document.createElement("div");
  el.className = "fnl-pin";
  el.setAttribute("role", "button");
  el.setAttribute("aria-label", p.title || p.category || "Place");
  el.tabIndex = 0;
  const bg  = p.color || "#0f766e";
  const iconPx = Math.round(sizePx * 0.55);
  const inner = svgFor(p.layer_id || p.category, iconPx);
  // Badge shape: rounded pill with a tiny stem so it still reads as a
  // "pin" from a distance while housing a crisp SVG glyph.
  el.style.cssText =
    `width:${sizePx}px;height:${sizePx}px;border-radius:${sizePx / 2}px ${sizePx / 2}px ${sizePx / 2}px 4px;` +
    `transform:rotate(-45deg);background:${bg};` +
    `display:flex;align-items:center;justify-content:center;` +
    `box-shadow:0 4px 10px rgba(0,0,0,0.35);border:2px solid #fff;cursor:pointer;`;
  const rot = document.createElement("span");
  rot.style.cssText = "transform:rotate(45deg);display:flex;align-items:center;justify-content:center;";
  rot.innerHTML = inner;
  el.appendChild(rot);
  if (p.pulse) el.style.animation = "fnl-pulse 1.4s infinite";
  if (onClick) {
    el.onclick = (e) => { e.stopPropagation(); onClick(p); };
    el.onkeydown = (e) => { if (e.key === "Enter") onClick(p); };
  }
  return el;
}

/**
 * Distinct visual for an ACTIVE EMERGENCY ALERT (not a POI).
 * Uses a flashing red halo + centered SOS glyph so a user can't confuse
 * it with a hospital / police / fire POI on the same map. Never renders
 * precise identity data — the caller controls what payload is exposed.
 */
export function emergencyAlertElement({ p, onClick }) {
  const el = document.createElement("div");
  el.className = "fnl-alert";
  el.setAttribute("role", "button");
  el.setAttribute("aria-label", "Active emergency alert");
  el.style.cssText =
    `width:44px;height:44px;border-radius:22px;background:#dc2626;color:#fff;` +
    `display:flex;align-items:center;justify-content:center;font-weight:900;` +
    `box-shadow:0 0 0 6px rgba(220,38,38,0.35),0 4px 12px rgba(0,0,0,0.35);` +
    `border:3px solid #fff;cursor:pointer;animation:fnl-pulse 1s infinite;`;
  el.textContent = "SOS";
  if (onClick) el.onclick = (e) => { e.stopPropagation(); onClick(p); };
  return el;
}

// Cluster bubble — kept in this file so every DOM element the map creates
// lives in one place.
export function clusterElement({ count, onClick }) {
  const el = document.createElement("div");
  el.className = "fnl-cluster";
  el.setAttribute("aria-label", `Cluster of ${count} places`);
  el.textContent = String(count);
  el.style.cssText =
    "min-width:34px;height:34px;padding:0 8px;border-radius:999px;background:#0f766e;color:#fff;" +
    "display:flex;align-items:center;justify-content:center;font-weight:900;font-size:12px;" +
    "box-shadow:0 4px 12px rgba(0,0,0,0.35);border:2px solid #fff;cursor:pointer;";
  if (onClick) el.onclick = onClick;
  return el;
}
